<template>
  <div v-if="info" class="imp-banner">
    <div class="imp-banner__content">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
      <span>
        Impersonating <strong>{{ info.target.email }}</strong>
        <span class="imp-banner__actor">(as {{ info.actor.email }})</span>
      </span>
      <span class="imp-banner__notice">
        Starting a new "View as user" session will end this one.
      </span>
      <span class="imp-banner__audit">All actions are audited</span>
    </div>
    <button class="imp-banner__exit" :disabled="exiting" @click="exit">
      {{ exiting ? 'Exiting...' : 'Exit impersonation' }}
    </button>
  </div>
</template>

<script>
export default {
  name: "ImpersonationBanner",
  data() {
    return {
      info: null,
      exiting: false,
    };
  },
  mounted() {
    this.loadInfo();
    // Listen for supersede signal from a new impersonation session.
    // When the admin starts impersonating someone else, this tab is
    // no longer valid and should close.
    this.handleStorage = (e) => {
      if (e.key === "impersonation:supersede") {
        try { window.close(); } catch (err) { /* ignore */ }
        // If close() is blocked (tab not opened by script), redirect to login.
        setTimeout(() => { window.location.href = "/login"; }, 100);
      }
    };
    window.addEventListener("storage", this.handleStorage);
  },
  beforeDestroy() {
    if (this.handleStorage) {
      window.removeEventListener("storage", this.handleStorage);
    }
  },
  methods: {
    loadInfo() {
      try {
        const raw = this.$cookies.get("impersonation_info");
        if (!raw) return;
        this.info = typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch (e) {
        this.info = null;
      }
    },
    async exit() {
      this.exiting = true;
      try {
        const baseURL = this.$config ? this.$config.baseURL : "http://pc.loc:8005";
        await this.$axios({
          method: "POST",
          url: `${baseURL}/auth/impersonate/stop?_format=json`,
          withCredentials: true,
        });
      } catch (e) {
        // Continue anyway — cookies will be cleared below.
      }
      // Ensure local cookie is cleared (backend clears its own).
      // Belt-and-braces cookie cleanup — clear from all possible domains/paths
      // so a fresh impersonation session doesn't see any stale cookies.
      const rootDomain = window.location.hostname.replace(/^vue\./, "");
      const variants = [
        { path: "/", domain: "." + rootDomain },
        { path: "/", domain: rootDomain },
        { path: "/", domain: window.location.hostname },
        { path: "/" },
      ];
      ["impersonation_sid", "impersonation_info"].forEach((name) => {
        variants.forEach((opts) => {
          try { this.$cookies.remove(name, opts); } catch (e) { /* ignore */ }
        });
        // document.cookie expiry as final fallback
        document.cookie = name + "=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=." + rootDomain;
        document.cookie = name + "=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      });
      window.location.href = "/login";
    },
  },
};
</script>

<style scoped lang="scss">
.imp-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 20px;
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  border-bottom: 2px solid #d97706;
  font-size: 1.3rem;
  color: #78350f;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &__content {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;

    svg { flex-shrink: 0; color: #92400e; }
    strong { color: #451a03; }
  }

  &__actor {
    color: #b45309;
    font-size: 1.2rem;
  }

  &__notice {
    color: #78350f;
    font-size: 1.15rem;
    background: rgba(255, 255, 255, 0.4);
    padding: 3px 10px;
    border-radius: 4px;
    border: 1px solid rgba(120, 53, 15, 0.2);
  }

  &__audit {
    color: #b45309;
    font-size: 1.1rem;
    font-style: italic;
    margin-left: auto;
  }

  &__exit {
    padding: 6px 18px;
    background: #78350f;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    flex-shrink: 0;

    &:hover:not(:disabled) { background: #451a03; }
    &:disabled { opacity: 0.7; cursor: default; }
  }
}
</style>
